'use client';

import { useState, useEffect, useMemo } from 'react';
import Image from 'next/image';
import { useCart } from '@/context/CartContext';
import { useTranslation } from '@/context/LanguageContext';
import { formatPrice, getVariantPrice, type Product } from '@/lib/products';
import { trackProductViewed, trackProductAdded } from '@/lib/analytics';
import { gaViewItem, gaAddToCart } from '@/lib/ga';
import ProductMediaCarousel from '@/components/ProductMediaCarousel';
import ProductCard from '@/components/ProductCard';
import DoubleWaveDivider from '@/components/DoubleWaveDivider';
import styles from './page.module.scss';

function sanitizeGuideHtml(html: string): string {
  return html
    .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, '')
    .replace(/<style[\s\S]*?>[\s\S]*?<\/style>/gi, '')
    .replace(/\son\w+="[^"]*"/gi, '')
    .replace(/\son\w+='[^']*'/gi, '');
}

interface ProductDetailProps {
  slug: string;
  initialProducts: Product[];
}

export default function ProductDetail({ slug, initialProducts }: ProductDetailProps) {
  const { t, currency, hydrated } = useTranslation();
  const [selectedVariant, setSelectedVariant] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const { addItem } = useCart();

  const translatedProducts = useMemo(
    () =>
      initialProducts.map((product) => {
        const tp = t.products[product.slug as keyof typeof t.products];
        if (!tp) return product;
        return {
          ...product,
          name: tp.name,
          shortName: tp.shortName,
          origin: tp.origin,
          roast: tp.roast,
          flavor: tp.flavor,
          variety: tp.variety,
          process: tp.process,
          description: tp.description,
          guide: { ...product.guide, ...tp.guide },
          guideRichText: (tp as { guideRichText?: string }).guideRichText ?? product.guideRichText,
        };
      }),
    [initialProducts, t]
  );

  const product = translatedProducts.find((p) => p.slug === slug);

  const variant = product?.variants[selectedVariant];
  const totalPrice = variant ? getVariantPrice(variant, currency) * quantity : 0;
  const lowestPrice = product
    ? Math.min(...product.variants.map((v) => getVariantPrice(v, currency)))
    : 0;
  const isComingSoon = !Number.isFinite(lowestPrice) || lowestPrice <= 0;

  useEffect(() => {
    if (product && variant) {
      trackProductViewed(
        { id: product.id, name: product.name },
        { size: variant.size, price: getVariantPrice(variant, currency) },
        currency
      );
      gaViewItem(
        {
          slug: product.slug,
          size: variant.size,
          name: product.name,
          price: getVariantPrice(variant, currency) / 100,
        },
        currency
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product?.id]);

  if (!product || !variant) {
    return (
      <section className={styles.notFound}>
        <div className="container">
          <h1>{t.productPage.notFoundTitle}</h1>
          <p>{t.productPage.notFoundBody}</p>
        </div>
      </section>
    );
  }

  const guideHtml = hydrated ? sanitizeGuideHtml(product.guideRichText?.trim() ?? '') : '';

  const handleAddToCart = () => {
    trackProductAdded(
      { id: product.id, name: product.name },
      { size: variant.size, price: getVariantPrice(variant, currency) },
      quantity,
      currency
    );
    gaAddToCart(
      {
        slug: product.slug,
        size: variant.size,
        name: product.name,
        price: getVariantPrice(variant, currency) / 100,
        quantity,
      },
      currency
    );
    addItem(
      {
        productId: product.id,
        name: product.name,
        size: variant.size,
        price: variant.price,
        priceEur: variant.priceEur,
        image: product.image,
        stripePriceId: variant.stripePriceId,
        stripePriceIdEur: variant.stripePriceIdEur,
      },
      quantity
    );
    setQuantity(1);
  };

  return (
    <section className={styles.product}>
      <div className={`container ${styles.grid}`}>
        <div className={styles.imageWrap}>
          {product.media || product.brewRecipe ? (
            <ProductMediaCarousel
              media={product.media ?? [{ type: 'image', src: product.image, alt: product.name }]}
              productName={product.name}
            />
          ) : (
            <div className={styles.imageFrame}>
              <Image
                src={product.image}
                alt={product.name}
                fill
                sizes="(max-width: 768px) 100vw, 50vw"
                priority
                style={{ objectFit: 'cover' }}
              />
            </div>
          )}
        </div>

        <div className={styles.details}>
          <h1 className={styles.name}>{product.name}</h1>
          {guideHtml && (
            <article className={styles.introRichText} dangerouslySetInnerHTML={{ __html: guideHtml }} />
          )}

          {isComingSoon ? (
            <button className={styles.addToCart} disabled>
              {t.productCard.comingSoon}
            </button>
          ) : (
            <>
              <div className={styles.sizeSelector}>
                <label className={styles.label}>{t.productPage.sizeLabel}</label>
                <div className={styles.sizes}>
                  {product.variants.map((v, index) => (
                    <button
                      key={v.size}
                      className={`${styles.sizeBtn} ${index === selectedVariant ? styles.active : ''}`}
                      onClick={() => setSelectedVariant(index)}
                    >
                      {v.size}
                    </button>
                  ))}
                </div>
              </div>

              <div className={styles.quantitySelector}>
                <label className={styles.label}>{t.productPage.quantityLabel}</label>
                <div className={styles.quantityControls}>
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    disabled={quantity <= 1}
                  >
                    &minus;
                  </button>
                  <span>{quantity}</span>
                  <button onClick={() => setQuantity(quantity + 1)}>+</button>
                </div>
              </div>

              <p className={styles.totalPrice}>{formatPrice(totalPrice, t)}</p>

              <button className={styles.addToCart} onClick={handleAddToCart}>
                {t.productPage.addToCart}
              </button>
            </>
          )}

          <div className={styles.info}>
            <h3>{t.productPage.coffeeDetails}</h3>
            <dl className={styles.specs}>
              <div>
                <dt>{t.productPage.specRoast}</dt>
                <dd>{product.roast}</dd>
              </div>
              <div>
                <dt>{t.productPage.specFlavor}</dt>
                <dd>{product.flavor}</dd>
              </div>
              <div>
                <dt>{t.productPage.specOrigin}</dt>
                <dd>{product.origin}</dd>
              </div>
              <div>
                <dt>{t.productPage.specVariety}</dt>
                <dd>{product.variety}</dd>
              </div>
              {product.altitude && (
                <div>
                  <dt>{t.productPage.specAltitude}</dt>
                  <dd>{product.altitude}</dd>
                </div>
              )}
              <div>
                <dt>{t.productPage.specProcess}</dt>
                <dd>{product.process}</dd>
              </div>
            </dl>
          </div>

        </div>
      </div>

      <DoubleWaveDivider flip />

      <section className={styles.relatedSection}>
        <div className={`container ${styles.related}`}>
          <h2 className={styles.relatedTitle}>Måske vil du synes godt om</h2>
          <div className={styles.relatedGrid}>
            {translatedProducts
              .filter((p) => p.id !== product.id)
              .map((p) => (
                <ProductCard key={p.id} product={p} />
              ))}
          </div>
        </div>
      </section>
    </section>
  );
}
